import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react';
import { config } from '../../../config';
import { LocalStorageKeys, getTransformedDataForPagination, objectToQueryParams } from '../../../utils';

export interface folderPayload {
    folder_name: string,
    description?: string,
    parent_object_type?: string,
    parent_object_id?: string,
    parent_folder_id?: string,
    links?: { object_ids: string[], object_type: string }[]
}

export const foldersAPI = createApi({
    reducerPath: "foldersAPI",
    baseQuery: retry(fetchBaseQuery({
        baseUrl: config.api_url + "dm/folder",
        prepareHeaders: (headers, { getState }) => {
            const token = localStorage.getItem(LocalStorageKeys.authToken);

            // If we have a token set in state, let's assume that we should be passing it.
            if (token) {
                headers.set('authorization', `Bearer ${token}`)
            }

            return headers
        },
    }), { maxRetries: 1 }),

    endpoints: (builder) => ({
        // Get Folders API
        getFolders: builder.query({
            query: ({
                object_id, object_type, parent_object_id, parent_object_type, object_sequence_id, sequence_id,
                page_no = 0, page_size = 10, sort_by = null, sort_order = null,
                all_folders = false, parent_folder_id,
            }) => ({
                url: objectToQueryParams({
                    object_id, object_type, object_sequence_id, sequence_id, parent_object_id, parent_object_type,
                    page_no: page_no + 1, page_size, sort_by, sort_order, all_folders, parent_folder_id
                }),
                method: "GET"
            }),
            transformResponse: (data: any, meta: { request: Request; response?: Response }) => {
                return getTransformedDataForPagination(data, meta?.response);
            },
        }),

        // Get Folder by ID API
        getFoldersById: builder.query({
            query: (id) => ({ url: `/${id}`, method: "GET" }),
        }),

        // Create Folder API
        createFolder: builder.mutation({
            query: (payload: folderPayload) => ({ method: "POST", body: payload }),
        }),

        // Update Folder API
        updateFolder: builder.mutation({
            query: (payload: { id: string, payload: folderPayload }) => ({ url: `/${payload.id}`, method: "PUT", body: payload.payload }),
        }),

        // Delete Folder API
        deleteFolder: builder.mutation({
            query: ({ id, force = false }) => ({ url: `/${id}?force=${force}`, method: "DELETE" }),
        }),

        // Import from Folder Template
        importFromFolderTemplate: builder.mutation({
            query: (payload: { parent_object_id: string, parent_object_type: string, template_id: string, only_children: boolean }) => ({ url: `/${payload.template_id}${objectToQueryParams(payload)}`, method: "POST" }),
        }),
    })
})

// Export hooks for usage in functional components
export const {
    useGetFoldersQuery, useLazyGetFoldersQuery, useGetFoldersByIdQuery, useLazyGetFoldersByIdQuery,
    useCreateFolderMutation, useUpdateFolderMutation, useDeleteFolderMutation, useImportFromFolderTemplateMutation
} = foldersAPI